import { ESCALA, deveRotular, escaparTexto, formatarValor } from "../tema";
import { estiloRotulo, montarSeries, opcoesBase } from "./base";

/**
 * Família 4 — linha temporal com rótulo em cada ponto.
 *
 * É o modelo dos acompanhamentos curtos ("Trigo - Bolsa de Chicago",
 * "Indicador Milho 30 dias"): poucos pontos, cada um com uma caixa
 * contendo a data em cima e o valor embaixo.
 *
 * As duas linhas do rótulo saem de um <br> no formatter — o mesmo
 * mecanismo do cabeçalho, que o Highcharts transforma em <tspan> e
 * portanto sobrevive à exportação.
 */
const modelo = {
  id: "linha-rotulada",
  label: "Linha com rótulos",
  descricao: "Poucos pontos, cada um com data e valor em caixa. Ex.: acompanhamento de 30 dias.",

  build(spec) {
    const base = opcoesBase(spec);
    const categorias = spec.dados?.categorias || [];
    const opcoesValor = spec.valores || {};
    const mostrarData = spec.extras?.mostrarData !== false;

    return {
      ...base,
      chart: { ...base.chart, type: "line", marginBottom: 10 * ESCALA },
      xAxis: {
        ...base.xAxis,
        categories: categorias,
        // A data já aparece dentro da caixa de cada ponto; repetir no eixo
        // só polui um gráfico deste tamanho.
        labels: { enabled: false },
        lineWidth: 0,
      },
      yAxis: { ...base.yAxis, maxPadding: 0.22, minPadding: 0.18 },
      plotOptions: {
        ...base.plotOptions,
        line: {
          lineWidth: 3 * ESCALA,
          marker: {
            enabled: true,
            radius: 3 * ESCALA,
            symbol: "circle",
            lineWidth: 0,
          },
          dataLabels: {
            ...estiloRotulo(spec),
            formatter() {
              const valor = formatarValor(this.y, opcoesValor);
              if (!mostrarData) return valor;
              const data = categorias[this.point.index] ?? "";
              return `${escaparTexto(data)}<br>${valor}`;
            },
          },
        },
      },
      series: montarSeries(spec, {
        showInLegend: Boolean(spec.legenda?.visivel),
      }).map((serie) => ({
        ...serie,
        // A linha usa cor sólida: um gradiente num traço fino vira ruído.
        color: spec.dados?.series?.[0]?.cor || "#FFFFFF",
        data: serie.data.map((valor, i) => ({
          y: valor,
          dataLabels: {
            enabled: deveRotular(i, serie.data.length, opcoesValor.exibirEm ?? "todos"),
          },
        })),
      })),
    };
  },

  exemplo() {
    return {
      modelo: "linha-rotulada",
      titulo: [
        { texto: "Trigo", cor: "#F0F000" },
        { texto: " - Bolsa de Chicago ", cor: "#FFFFFF" },
        { texto: "(US$/bushel)", cor: "#F0F000", tamanho: 8 },
      ],
      subtitulo: [{ texto: "(contrato dez/26)", cor: "#FFFFFF" }],
      fonte: "Fonte: RURAL BUSINESS/CME Group",
      fundo: "#0B4C8C",
      fundoPlot: "nenhum",
      dados: {
        categorias: ["26-ago", "27-ago", "28-ago", "31-ago", "1-set", "2-set", "3-set"],
        series: [
          {
            nome: "Trigo",
            cor: "#FFC000",
            corFim: "#FFC000",
            valores: [7.48, 7.61, 7.84, 7.74, 7.83, 7.74, 7.54],
          },
        ],
      },
      legenda: { visivel: false },
      valores: { decimais: 2, prefixo: "", sufixo: "", exibirEm: "todos" },
      rotulo: { corTexto: "#000000", corFundo: "#FFFFFF", corBorda: "#000000" },
      extras: { mostrarData: true },
    };
  },
};

export default modelo;
